import { useCallback, useEffect, useState } from 'react';
import { pdApi } from '../api';
import '../screens.css';
import { HelpPanel } from '../HelpPanel';
import { FIN_CONFIGURATION, SCENARIO_SETUP } from '../helpContent';

/**
 * Financial Modeling → Configuration, and → Scenario Setup.
 *
 * Cloned from `views/value-roi/configuration.ejs` and
 * `views/npv-value-model/scenario-setup.ejs`.
 *
 * THE THRESHOLDS ON THIS SCREEN DECIDE WHAT COUNTS AS VALUABLE. ROI at or above the high
 * threshold is High Value, at or above the medium threshold is Medium, and a payback
 * longer than the maximum makes it Low regardless of ROI. Change them and every
 * opportunity's classification moves — which is the point, but worth knowing before
 * touching them.
 *
 * The sensitivity factors drive the best/worst case ROI: best case is a cheaper build and
 * higher savings, worst case the reverse. They apply to the STORED metrics only — the
 * Version Comparison screen carries its own hard-coded copies of the same four numbers,
 * which is a source inconsistency logged in docs/DEVIATIONS.md.
 */

type Dict = Record<string, unknown>;

interface Settings {
  high_value_roi_threshold: string;
  medium_value_roi_threshold: string;
  max_payback_years_for_viability: string;
  sensitivity_build_cost_min_factor: string;
  sensitivity_build_cost_max_factor: string;
  sensitivity_savings_min_factor: string;
  sensitivity_savings_max_factor: string;
}

const FIELDS: { key: keyof Settings; label: string; hint?: string }[] = [
  { key: 'high_value_roi_threshold', label: 'High value ROI threshold (%)' },
  { key: 'medium_value_roi_threshold', label: 'Medium value ROI threshold (%)' },
  {
    key: 'max_payback_years_for_viability',
    label: 'Maximum payback for viability (years)',
    hint: 'Beyond this, an opportunity is Low Value however good its ROI.',
  },
  { key: 'sensitivity_build_cost_min_factor', label: 'Best case build-cost factor' },
  { key: 'sensitivity_build_cost_max_factor', label: 'Worst case build-cost factor' },
  { key: 'sensitivity_savings_min_factor', label: 'Worst case savings factor' },
  { key: 'sensitivity_savings_max_factor', label: 'Best case savings factor' },
];

export function FinancialConfiguration() {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [costTypes, setCostTypes] = useState<Dict[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [editingType, setEditingType] = useState<Dict | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = (await pdApi.financial.configuration()) as Dict;
      const s = data.settings as Dict;
      const str = (v: unknown) => (v === null || v === undefined ? '' : String(v));
      setSettings({
        high_value_roi_threshold: str(s.high_value_roi_threshold),
        medium_value_roi_threshold: str(s.medium_value_roi_threshold),
        max_payback_years_for_viability: str(s.max_payback_years_for_viability),
        sensitivity_build_cost_min_factor: str(s.sensitivity_build_cost_min_factor),
        sensitivity_build_cost_max_factor: str(s.sensitivity_build_cost_max_factor),
        sensitivity_savings_min_factor: str(s.sensitivity_savings_min_factor),
        sensitivity_savings_max_factor: str(s.sensitivity_savings_max_factor),
      });
      setCostTypes((data.costTypes as Dict[]) ?? []);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const run = async (fn: () => Promise<unknown>, ok?: string) => {
    setError(null);
    setNotice(null);
    try {
      await fn();
      if (ok) setNotice(ok);
      setEditingType(null);
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  if (loading || !settings) return <div className="pd-screen pd-loading">Loading…</div>;

  return (
    <div className="pd-screen">
      <div className="pd-page-header">
        <div>
          <h1 className="pd-page-header__title">Value Model Configuration</h1>
          <p className="pd-page-header__subtitle">
            Thresholds and sensitivity factors used by every MVP value calculation.
          </p>
        </div>
        <div className="pd-page-header__actions">
          <button
            type="button"
            className="pd-btn pd-btn--primary"
            onClick={() =>
              run(() => pdApi.financial.saveSettings(settings), 'Value model settings saved successfully')
            }
          >
            Save settings
          </button>
        </div>
        <div className="pd-page-header__actions">
          <HelpPanel help={FIN_CONFIGURATION} />
        </div>
      </div>

      {error ? <div className="pd-error">{error}</div> : null}
      {notice ? <div className="pd-notice">{notice}</div> : null}

      <h2 className="pd-section-title">Value Classification Thresholds</h2>
      <div className="pd-form-grid">
        <section className="pd-form-section">
          {FIELDS.slice(0, 3).map((f) => (
            <div className="pd-field" key={f.key}>
              <label htmlFor={`fc-${f.key}`}>{f.label}</label>
              <input
                id={`fc-${f.key}`}
                type="number"
                step="0.01"
                value={settings[f.key]}
                onChange={(e) => setSettings({ ...settings, [f.key]: e.target.value })}
              />
              {f.hint ? <span className="pd-hint">{f.hint}</span> : null}
            </div>
          ))}
        </section>

        <section className="pd-form-section">
          <h2 className="pd-section-title">Sensitivity Analysis Parameters</h2>
          {FIELDS.slice(3).map((f) => (
            <div className="pd-field" key={f.key}>
              <label htmlFor={`fc-${f.key}`}>{f.label}</label>
              <input
                id={`fc-${f.key}`}
                type="number"
                step="0.01"
                value={settings[f.key]}
                onChange={(e) => setSettings({ ...settings, [f.key]: e.target.value })}
              />
            </div>
          ))}
          <span className="pd-hint">
            Best case: build × {settings.sensitivity_build_cost_min_factor}, savings ×{' '}
            {settings.sensitivity_savings_max_factor}. Worst case: build ×{' '}
            {settings.sensitivity_build_cost_max_factor}, savings ×{' '}
            {settings.sensitivity_savings_min_factor}.
          </span>
        </section>
      </div>

      <h2 className="pd-section-title">Cost Types</h2>
      <div className="pd-page-header__actions" style={{ marginBottom: 12 }}>
        <button
          type="button"
          className="pd-btn"
          onClick={() =>
            setEditingType({ name: '', category: 'other', default_frequency: 'one-time' })
          }
        >
          New cost type
        </button>
      </div>
      <div className="pd-table-wrap">
        <table className="pd-table">
          <thead>
            <tr>
              <th style={{ width: 240 }}>Name</th>
              <th style={{ width: 160 }}>Category</th>
              <th style={{ width: 140 }}>Default Frequency</th>
              <th style={{ width: 130 }}>Depreciable</th>
              <th style={{ width: 110 }}>Active</th>
              <th style={{ width: 130 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {costTypes.map((c) => (
              <tr key={String(c.id)}>
                <td>{String(c.name)}</td>
                <td>{String(c.category)}</td>
                <td>{String(c.default_frequency ?? '—')}</td>
                <td>
                  {c.is_depreciable
                    ? `Yes${c.default_depreciation_years ? ` · ${String(c.default_depreciation_years)}y` : ''}`
                    : 'No'}
                </td>
                <td>{c.is_active ? 'Active' : 'Inactive'}</td>
                <td>
                  <div className="pd-row-actions">
                    <button
                      type="button"
                      className="pd-icon-btn"
                      title="Edit"
                      onClick={() => setEditingType({ ...c })}
                    >
                      ✏️
                    </button>
                    <button
                      type="button"
                      className="pd-btn"
                      onClick={() => run(() => pdApi.financial.toggleCostType(Number(c.id)))}
                    >
                      {c.is_active ? 'Disable' : 'Enable'}
                    </button>
                    <button
                      type="button"
                      className="pd-icon-btn"
                      title={
                        c.is_default
                          ? 'A default cost type cannot be deleted'
                          : 'Delete (refused if in use)'
                      }
                      disabled={Boolean(c.is_default)}
                      onClick={() => run(() => pdApi.financial.deleteCostType(Number(c.id)))}
                    >
                      🗑️
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editingType ? (
        <div className="pd-modal-backdrop" onClick={() => setEditingType(null)}>
          <div className="pd-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pd-modal__head">
              <h2 className="pd-modal__title">
                {editingType.id ? 'Edit cost type' : 'New cost type'}
              </h2>
              <button type="button" className="pd-icon-btn" onClick={() => setEditingType(null)}>
                ✕
              </button>
            </div>
            <div className="pd-modal__body">
              <div className="pd-field">
                <label htmlFor="ct-name">Name *</label>
                <input
                  id="ct-name"
                  type="text"
                  value={String(editingType.name ?? '')}
                  onChange={(e) => setEditingType({ ...editingType, name: e.target.value })}
                />
              </div>
              <div className="pd-field">
                <label htmlFor="ct-cat">Category</label>
                <input
                  id="ct-cat"
                  type="text"
                  value={String(editingType.category ?? '')}
                  onChange={(e) => setEditingType({ ...editingType, category: e.target.value })}
                />
              </div>
              <div className="pd-field">
                <label htmlFor="ct-freq">Default frequency</label>
                <select
                  id="ct-freq"
                  value={String(editingType.default_frequency ?? '')}
                  onChange={(e) =>
                    setEditingType({ ...editingType, default_frequency: e.target.value })
                  }
                >
                  <option value="one-time">one-time</option>
                  <option value="monthly">monthly</option>
                  <option value="annual">annual</option>
                </select>
                <span className="pd-hint">
                  This is what splits the cost pickers into one-time and recurring.
                </span>
              </div>
              <div className="pd-field pd-field--inline">
                <input
                  id="ct-dep"
                  type="checkbox"
                  checked={Boolean(editingType.is_depreciable)}
                  onChange={(e) =>
                    setEditingType({ ...editingType, is_depreciable: e.target.checked ? 1 : 0 })
                  }
                />
                <label htmlFor="ct-dep">Depreciable</label>
              </div>
              {editingType.is_depreciable ? (
                <div className="pd-field">
                  <label htmlFor="ct-years">Default depreciation years</label>
                  <input
                    id="ct-years"
                    type="number"
                    value={String(editingType.default_depreciation_years ?? '')}
                    onChange={(e) =>
                      setEditingType({
                        ...editingType,
                        default_depreciation_years: e.target.value,
                      })
                    }
                  />
                  <span className="pd-hint">Defaults to 3 when left empty.</span>
                </div>
              ) : null}
            </div>
            <div className="pd-modal__foot">
              <button type="button" className="pd-btn" onClick={() => setEditingType(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="pd-btn pd-btn--primary"
                onClick={() => run(() => pdApi.financial.saveCostType(editingType), 'Cost type saved')}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Financial Modeling → Scenario Setup.
 *
 * Scenario types are what a model VERSION can be. They are split by usage context: the MVP
 * set and the NPV set have their own single default each.
 *
 * Both NPV scenarios ship INACTIVE, which is the source's out-of-box state — until one is
 * activated here, the NPV screens have no scenario to attach costs to. That is reproduced
 * rather than quietly corrected, and called out on screen so it is discoverable.
 */
export function ScenarioSetup() {
  const [scenarios, setScenarios] = useState<Dict[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [editing, setEditing] = useState<Dict | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await pdApi.financial.scenarioTypes();
      setScenarios(data.scenarios);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const run = async (fn: () => Promise<unknown>, ok?: string) => {
    setError(null);
    setNotice(null);
    try {
      await fn();
      if (ok) setNotice(ok);
      setEditing(null);
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const noActiveNpv = scenarios.some((s) => s.usage_context === 'NPV')
    && !scenarios.some((s) => s.usage_context === 'NPV' && s.is_active);

  return (
    <div className="pd-screen">
      <div className="pd-page-header">
        <div>
          <h1 className="pd-page-header__title">Scenario Setup</h1>
          <p className="pd-page-header__subtitle">
            The scenarios a financial model version can be — one set for the MVP model, one
            for NPV, each with its own default.
          </p>
        </div>
        <div className="pd-page-header__actions">
          <button
            type="button"
            className="pd-btn pd-btn--primary"
            onClick={() =>
              setEditing({ code: '', name: '', usage_context: 'MVP', sort_order: 1, is_active: true })
            }
          >
            New scenario
          </button>
        </div>
        <div className="pd-page-header__actions">
          <HelpPanel help={SCENARIO_SETUP} />
        </div>
      </div>

      {error ? <div className="pd-error">{error}</div> : null}
      {notice ? <div className="pd-notice">{notice}</div> : null}
      {noActiveNpv ? (
        <div className="pd-notice">
          No NPV scenario is active, so the NPV screens have nothing to attach costs to.
          Activate one below to use them. (This is the shipped default.)
        </div>
      ) : null}

      {loading ? (
        <div className="pd-loading">Loading…</div>
      ) : (
        <div className="pd-table-wrap">
          <table className="pd-table">
            <thead>
              <tr>
                <th style={{ width: 140 }}>Code</th>
                <th style={{ width: 200 }}>Name</th>
                <th>Description</th>
                <th style={{ width: 100 }}>Context</th>
                <th style={{ width: 80 }}>Order</th>
                <th style={{ width: 90 }}>Active</th>
                <th style={{ width: 90 }}>Default</th>
                <th style={{ width: 110 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {scenarios.map((s) => (
                <tr key={String(s.id)}>
                  <td>{String(s.code)}</td>
                  <td>{String(s.name)}</td>
                  <td>{String(s.description ?? '')}</td>
                  <td>{String(s.usage_context)}</td>
                  <td className="pd-num">{String(s.sort_order)}</td>
                  <td>{s.is_active ? 'Yes' : 'No'}</td>
                  <td>{s.is_default ? '★' : ''}</td>
                  <td>
                    <div className="pd-row-actions">
                      <button
                        type="button"
                        className="pd-icon-btn"
                        title="Edit"
                        onClick={() => setEditing({ ...s })}
                      >
                        ✏️
                      </button>
                      <button
                        type="button"
                        className="pd-icon-btn"
                        title={
                          s.is_default
                            ? 'The default scenario cannot be deleted'
                            : 'Delete (refused if versions use it)'
                        }
                        disabled={Boolean(s.is_default)}
                        onClick={() => run(() => pdApi.financial.deleteScenarioType(Number(s.id)))}
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing ? (
        <div className="pd-modal-backdrop" onClick={() => setEditing(null)}>
          <div className="pd-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pd-modal__head">
              <h2 className="pd-modal__title">
                {editing.id ? 'Edit scenario' : 'New scenario'}
              </h2>
              <button type="button" className="pd-icon-btn" onClick={() => setEditing(null)}>
                ✕
              </button>
            </div>
            <div className="pd-modal__body">
              <div className="pd-field">
                <label htmlFor="st-code">Code *</label>
                <input
                  id="st-code"
                  type="text"
                  value={String(editing.code ?? '')}
                  readOnly={Boolean(editing.id)}
                  onChange={(e) => setEditing({ ...editing, code: e.target.value })}
                />
                {editing.id ? (
                  <span className="pd-hint">
                    Not editable — model versions reference the code by value.
                  </span>
                ) : null}
              </div>
              <div className="pd-field">
                <label htmlFor="st-name">Name *</label>
                <input
                  id="st-name"
                  type="text"
                  value={String(editing.name ?? '')}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                />
              </div>
              <div className="pd-field">
                <label htmlFor="st-desc">Description</label>
                <textarea
                  id="st-desc"
                  value={String(editing.description ?? '')}
                  onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div className="pd-field">
                  <label htmlFor="st-ctx">Usage context</label>
                  <select
                    id="st-ctx"
                    value={String(editing.usage_context ?? 'MVP')}
                    onChange={(e) => setEditing({ ...editing, usage_context: e.target.value })}
                  >
                    <option value="MVP">MVP</option>
                    <option value="NPV">NPV</option>
                  </select>
                </div>
                <div className="pd-field">
                  <label htmlFor="st-order">Sort order</label>
                  <input
                    id="st-order"
                    type="number"
                    value={String(editing.sort_order ?? 1)}
                    onChange={(e) => setEditing({ ...editing, sort_order: e.target.value })}
                  />
                </div>
              </div>
              <div className="pd-field pd-field--inline">
                <input
                  id="st-active"
                  type="checkbox"
                  checked={Boolean(editing.is_active)}
                  onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })}
                />
                <label htmlFor="st-active">Active</label>
              </div>
              <div className="pd-field pd-field--inline">
                <input
                  id="st-default"
                  type="checkbox"
                  checked={Boolean(editing.is_default)}
                  onChange={(e) => setEditing({ ...editing, is_default: e.target.checked })}
                />
                <label htmlFor="st-default">Default for this context</label>
              </div>
            </div>
            <div className="pd-modal__foot">
              <button type="button" className="pd-btn" onClick={() => setEditing(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="pd-btn pd-btn--primary"
                onClick={() =>
                  run(
                    () =>
                      editing.id
                        ? pdApi.financial.updateScenarioType(Number(editing.id), editing)
                        : pdApi.financial.createScenarioType(editing),
                    'Scenario saved',
                  )
                }
              >
                Save
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
